// src/utils/amplitude.ts
import { init, track, identify, Identify } from "@amplitude/analytics-browser";

// Initialize Amplitude with API key and configuration
let initialized = false;

export function initializeAmplitude() {
  if (initialized) {
    console.log("Amplitude already initialized, skipping");
    return;
  }

  try {
    console.log("Initializing Amplitude Browser SDK");
    init("679d1f34835a0d81d41f5d0e3ec68e78", undefined, {
      logLevel: 0,
      defaultTracking: {
        pageViews: true,
        sessions: true,
        formInteractions: true,
        fileDownloads: true,
      },
    });
    initialized = true;
    console.log("Amplitude initialized successfully");
  } catch (error) {
    console.error("Error initializing Amplitude:", error);
  }
}

// Track events with consistent logging
export function trackEvent(
  eventName: string,
  eventProperties?: Record<string, any>
) {
  try {
    console.log(`📊 Tracking event: ${eventName}`, eventProperties);

    // Ensure Amplitude is initialized
    if (!initialized) {
      console.log("Amplitude not initialized, initializing now");
      initializeAmplitude();
    }

    // Send the event
    track(eventName, eventProperties);

    // For debugging
    console.log(`[Amplitude] Sent event: ${eventName}`, {
      event_type: eventName,
      event_properties: eventProperties,
      time: new Date().toISOString(),
    });

    return true;
  } catch (error) {
    console.error(`Error tracking event ${eventName}:`, error);
    return false;
  }
}

// Identify users with proper user properties
export function identifyUser(
  userId: string,
  userProperties?: Record<string, any>
) {
  try {
    if (!userId) {
      console.warn("No userId provided to identifyUser");
      return false;
    }

    console.log(`👤 Identifying user: ${userId}`, userProperties);

    // Ensure Amplitude is initialized
    if (!initialized) {
      console.log("Amplitude not initialized, initializing now");
      initializeAmplitude();
    }

    // Set user properties using Identify object
    if (userProperties) {
      const identifyObj = new Identify();

      Object.entries(userProperties).forEach(([key, value]) => {
        identifyObj.set(key, value);
      });

      identify(identifyObj);
    }

    // Track identify event
    track("$identify", {
      user_id: userId,
      ...userProperties,
    });

    return true;
  } catch (error) {
    console.error("Error identifying user:", error);
    return false;
  }
}

// Reset user - call this when user logs out
export function resetUser() {
  try {
    console.log("Resetting Amplitude user");
    // Track logout event before resetting
    track("$logout", {
      timestamp: new Date().toISOString(),
    });
    return true;
  } catch (error) {
    console.error("Error resetting user:", error);
    return false;
  }
}

// Function to directly test Amplitude connection
export function testAmplitudeConnection() {
  try {
    console.log("Testing Amplitude connection...");

    // Ensure Amplitude is initialized
    if (!initialized) {
      console.log("Amplitude not initialized, initializing now");
      initializeAmplitude();
    }

    // Send a test event
    track("Test Connection", {
      test_id: Math.random().toString(36).substring(2, 15),
      timestamp: new Date().toISOString(),
    });

    console.log("Test event sent to Amplitude");
    return true;
  } catch (error) {
    console.error("Error testing Amplitude connection:", error);
    return false;
  }
}

export default {
  initializeAmplitude,
  trackEvent,
  identifyUser,
  resetUser,
  testAmplitudeConnection,
};
